import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
} from '@mui/material';
import { Close, Save, Add } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { createPaymentMethod, updatePaymentMethod } from '../../store/slices/paymentMethodsSlice';
import { PaymentMethodData, PaymentMethodForm, PaymentMethodUpdateForm } from '../../types';
import { toast } from 'react-toastify';

interface PaymentMethodFormModalProps {
  open: boolean;
  onClose: () => void;
  paymentMethod?: PaymentMethodData | null;
  mode: 'create' | 'edit';
}

const PaymentMethodFormModal: React.FC<PaymentMethodFormModalProps> = ({
  open,
  onClose,
  paymentMethod,
  mode,
}) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state: RootState) => state.paymentMethods);

  const [formData, setFormData] = useState<PaymentMethodForm>({
    name: '',
    accountNumber: '',
    verifyAccountName: '',
    justVerifyProvider: 'none',
    description: '',
    type: 'bank',
    icon: '🏦',
    sortOrder: 1,
    exchangeRate: 135,
    cryptoCurrency: 'USDT',
    network: 'Binance Pay',
    binanceIdType: 'pay_id',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (paymentMethod && mode === 'edit') {
      setFormData({
        name: paymentMethod.name,
        accountNumber: paymentMethod.accountNumber,
        verifyAccountName: paymentMethod.verifyAccountName || '',
        justVerifyProvider: paymentMethod.justVerifyProvider || 'none',
        description: paymentMethod.description,
        type: paymentMethod.type,
        icon: paymentMethod.icon,
        sortOrder: paymentMethod.sortOrder,
        exchangeRate: paymentMethod.exchangeRate !== undefined ? paymentMethod.exchangeRate : 135,
        cryptoCurrency: paymentMethod.cryptoCurrency || 'USDT',
        network: paymentMethod.network || 'Binance Pay',
        binanceIdType: paymentMethod.binanceIdType || 'pay_id',
      });
    } else {
      setFormData({
        name: '',
        accountNumber: '',
        verifyAccountName: '',
        justVerifyProvider: 'none',
        description: '',
        type: 'bank',
        icon: '🏦',
        sortOrder: 1,
        exchangeRate: 135,
        cryptoCurrency: 'USDT',
        network: 'Binance Pay',
        binanceIdType: 'pay_id',
      });
    }
    setErrors({});
  }, [paymentMethod, mode, open]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.accountNumber.trim()) {
      newErrors.accountNumber = formData.type === 'crypto' ? 'Binance Pay ID / UID is required' : 'Account number is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.icon.trim()) {
      newErrors.icon = 'Icon is required';
    }

    if (formData.sortOrder < 1) {
      newErrors.sortOrder = 'Sort order must be at least 1';
    }

    if (formData.type === 'crypto' && (!formData.exchangeRate || formData.exchangeRate <= 0)) {
      newErrors.exchangeRate = 'Exchange rate must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      if (mode === 'create') {
        await dispatch(createPaymentMethod(formData) as any);
        toast.success('Payment method created successfully');
      } else if (paymentMethod) {
        const updateData: PaymentMethodUpdateForm = {
          ...formData,
          isActive: paymentMethod.isActive,
        };
        await dispatch(updatePaymentMethod({ methodId: paymentMethod.id, methodData: updateData }) as any);
        toast.success('Payment method updated successfully');
      }
      onClose();
    } catch (error) {
      toast.error('Failed to save payment method');
    }
  };

  const handleInputChange = (field: keyof PaymentMethodForm, value: any) => {
    setFormData(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'type' && value === 'crypto') {
        if (prev.icon === '🏦') updated.icon = '₿';
        if (!prev.name) updated.name = 'Binance Pay';
        if (!prev.description) updated.description = 'Deposit USDT using Binance Pay. Instant conversion to Birr.';
      }
      return updated;
    });
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const paymentTypeOptions = [
    { value: 'bank', label: 'Bank', icon: '🏦' },
    { value: 'mobile_money', label: 'Mobile Money', icon: '📱' },
    { value: 'crypto', label: 'Cryptocurrency (Binance)', icon: '₿' },
    { value: 'other', label: 'Other', icon: '💳' },
  ];

  const iconOptions = [
    '🏦', '📱', '₿', '💳', '💰', '💎', '🏛️', '🏢', '🏪', '🏬', '🏭', '🏰', '🏯', '🏰', '🏯'
  ];

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      scroll="paper"
      sx={{
        '& .MuiDialog-paper': {
          margin: { xs: 1, sm: 2 },
          maxHeight: { xs: 'calc(100dvh - 16px)', sm: '90vh' },
          width: { xs: 'calc(100% - 16px)', sm: 'auto' },
          display: 'flex',
          flexDirection: 'column',
        },
      }}
     >
      <DialogTitle sx={{ 
        padding: { xs: 2, sm: 3 },
        paddingBottom: { xs: 1, sm: 2 }
      }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
            {mode === 'create' ? 'Create Payment Method' : 'Edit Payment Method'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers sx={{
        overflowY: 'auto',
        flex: '1 1 auto',
        minHeight: 0, 
        padding: { xs: 2, sm: 3 },
        '&.MuiDialogContent-root': {
          paddingTop: { xs: 1, sm: 2 },
        }
      }}>
        <Box sx={{ mt: { xs: 0, sm: 2 } }}>
          {/* Method Type & Name */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 2fr' }, 
            gap: 2, 
            mb: 2 
          }}>
            <FormControl fullWidth error={!!errors.type}>
              <InputLabel>Type</InputLabel>
              <Select
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                label="Type"
              >
                {paymentTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{option.icon}</span>
                      <span>{option.label}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Method Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name || (formData.type === 'crypto' ? 'e.g. Binance Pay (USDT)' : 'e.g. Commercial Bank of Ethiopia')}
              fullWidth
              required
            />
          </Box>

          {/* Account Number / Binance Pay ID */}
          <Box sx={{ mb: 2 }}>
            <TextField
              label={formData.type === 'crypto' ? 'Binance Pay ID / UID' : 'Account Number'}
              value={formData.accountNumber}
              onChange={(e) => handleInputChange('accountNumber', e.target.value)}
              error={!!errors.accountNumber}
              helperText={errors.accountNumber || (formData.type === 'crypto' ? 'Enter your 9-digit Binance Pay ID or UID where players will send crypto' : 'Account or mobile number for transfers')}
              fullWidth
              required
            />
          </Box>

          {/* Crypto Specific Settings */}
          {formData.type === 'crypto' ? (
            <Box sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'primary.light', borderRadius: 2, bgcolor: 'action.hover' }}>
              <Typography variant="subtitle2" color="primary" fontWeight="bold" sx={{ mb: 1.5 }}>
                🪙 Binance &amp; Crypto Settings
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Players can deposit USDT via Binance Pay to your ID. The Telegram Bot and API will automatically convert USDT into ETB (Birr) at this exchange rate. You can change this rate at any time!
              </Alert>
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2,
                mb: 2
              }}>
                <TextField
                  label="Exchange Rate (1 USDT = ? ETB)"
                  type="number"
                  value={formData.exchangeRate ?? 135}
                  onChange={(e) => handleInputChange('exchangeRate', parseFloat(e.target.value) || 0)}
                  error={!!errors.exchangeRate}
                  helperText={errors.exchangeRate || 'ETB credited to player wallet per 1 USDT (e.g. 135)'}
                  fullWidth
                  required
                  inputProps={{ min: 0.1, step: 'any' }}
                />
                <FormControl fullWidth>
                  <InputLabel>ID Type</InputLabel>
                  <Select
                    value={formData.binanceIdType || 'pay_id'}
                    onChange={(e) => handleInputChange('binanceIdType', e.target.value)}
                    label="ID Type"
                  >
                    <MenuItem value="pay_id">Binance Pay ID</MenuItem>
                    <MenuItem value="binance_id">Binance UID (User ID)</MenuItem>
                    <MenuItem value="email">Binance Registered Email</MenuItem>
                    <MenuItem value="phone">Binance Registered Phone</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 2
              }}>
                <TextField
                  label="Crypto Currency"
                  value={formData.cryptoCurrency || 'USDT'}
                  onChange={(e) => handleInputChange('cryptoCurrency', e.target.value)}
                  helperText="Default: USDT"
                  fullWidth
                />
                <TextField
                  label="Network / Transfer Type"
                  value={formData.network || 'Binance Pay'}
                  onChange={(e) => handleInputChange('network', e.target.value)}
                  helperText="Default: Binance Pay (Zero Gas Fees)"
                  fullWidth
                />
              </Box>
            </Box>
          ) : (
            /* Bank / JustVerify Settings */
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
              mb: 2
            }}>
              <TextField
                label="Verify account name"
                value={formData.verifyAccountName || ''}
                onChange={(e) => handleInputChange('verifyAccountName', e.target.value)}
                helperText="Bank account holder name JustVerify / SMS must match"
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>JustVerify provider</InputLabel>
                <Select
                  value={formData.justVerifyProvider || 'none'}
                  onChange={(e) => handleInputChange('justVerifyProvider', e.target.value)}
                  label="JustVerify provider"
                >
                  <MenuItem value="none">Off — Instant Telebirr (SMS receiver)</MenuItem>
                  <MenuItem value="cbe">CBE</MenuItem>
                  <MenuItem value="boa">Bank of Abyssinia</MenuItem>
                  <MenuItem value="telebirr">Telebirr — Normal (JustVerify proof)</MenuItem>
                  <MenuItem value="cbebirr">CBE Birr</MenuItem>
                </Select>
                <FormHelperText>
                  Instant Telebirr: name it &quot;Instant Telebirr&quot; + Off.
                  Normal Telebirr: name it &quot;Telebirr&quot; + Telebirr provider.
                </FormHelperText>
              </FormControl>
            </Box>
          )}

          <TextField
            label="Description / User Instructions"
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            error={!!errors.description}
            helperText={errors.description}
            fullWidth
            multiline
            rows={3}
            sx={{ mb: 2 }}
            required
          />

          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, 
            gap: 2, 
            mb: 2 
          }}>
            <FormControl fullWidth error={!!errors.icon}>
              <InputLabel>Icon</InputLabel>
              <Select
                value={formData.icon}
                onChange={(e) => handleInputChange('icon', e.target.value)}
                label="Icon"
              >
                {iconOptions.map((icon) => (
                  <MenuItem key={icon} value={icon}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{icon}</span>
                      <span>{icon}</span>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Sort Order"
              type="number"
              value={formData.sortOrder}
              onChange={(e) => handleInputChange('sortOrder', parseInt(e.target.value) || 1)}
              error={!!errors.sortOrder}
              helperText={errors.sortOrder}
              fullWidth
              required
              inputProps={{ min: 1 }}
            />
          </Box>

          {Object.keys(errors).length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Please fix the errors above before submitting.
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        flexDirection: { xs: 'column', sm: 'row' }, 
        gap: { xs: 1, sm: 0 },
        padding: { xs: 2, sm: 1 },
        '& > *': {
          width: { xs: '100%', sm: 'auto' },
        }
      }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          variant="outlined"
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : mode === 'create' ? <Add /> : <Save />}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          {loading ? 'Saving...' : mode === 'create' ? 'Create' : 'Update'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentMethodFormModal; 